import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Chip, Alert, LinearProgress,
  IconButton, Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
  TablePagination, Stack, useMediaQuery, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  ToggleButtonGroup, ToggleButton, FormControlLabel, Checkbox,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  PlayArrow as ResumeIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Outbox as OutreachIcon,
  OpenInNew as OpenInNewIcon,
  Check as ApproveIcon,
  Refresh as RegenerateIcon,
  Send as SendAllIcon,
  HourglassBottom as CancellingIcon,
  Block as BlockIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import {
  getAutoOutreachRun, listAutoOutreachItems, cancelAutoOutreachRun, resumeAutoOutreachRun,
  updateAutoOutreachRun, deleteAutoOutreachRun, approveAutoOutreachItem,
  regenerateAutoOutreachItem, sendAllReadyForJob,
  unsubscribeLead, unsubscribeLeadContact,
  getInteractionDetail,
} from '../services/api';
import { useDialog } from '../components/DialogProvider';

const STATUS_CHIP_COLOR = { Pending: 'default', Running: 'info', Cancelling: 'warning', Completed: 'success', Failed: 'error', Cancelled: 'warning' };
const ITEM_STATUS_COLOR = { Pending: 'default', Processing: 'info', Scheduled: 'success', NeedsReview: 'warning', Sent: 'success', Failed: 'error', Skipped: 'default' };
const STATUS_FILTERS = ['All', 'Pending', 'Processing', 'Scheduled', 'NeedsReview', 'Sent', 'Failed', 'Skipped'];

function fmtSecs(s) { if (s < 60) return `${s}s`; const m = Math.floor(s / 60); return `${m}m ${s % 60}s`; }
function fmtDuration(start, end, accumulatedSeconds) {
  const acc = accumulatedSeconds || 0;
  if (end) { if (acc > 0) return fmtSecs(acc); if (start) return fmtSecs(Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000))); return '—'; }
  if (!start) return acc > 0 ? fmtSecs(acc) : '—';
  return fmtSecs(acc + Math.max(0, Math.round((Date.now() - new Date(start).getTime()) / 1000)));
}
const isTerminal = (s) => s === 'Completed' || s === 'Failed' || s === 'Cancelled';

const EMAIL_BASE_STYLES = `
  html, body { margin: 0; padding: 16px; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 13px; line-height: 1.6; color: #444; }
  img { max-width: 100%; height: auto; }
  a { color: #4a90e2; }
  table { border-collapse: collapse; }
`;
function buildPreviewDoc(html) {
  if (!html) return `<!doctype html><html><head><style>${EMAIL_BASE_STYLES}</style></head><body></body></html>`;
  if (/<!doctype/i.test(html)) return html;
  return `<!doctype html><html><head><meta charset="utf-8"><style>${EMAIL_BASE_STYLES}</style></head><body>${html}</body></html>`;
}

export default function AutoEmailOutreachJobDetailPage() {
  const { id } = useParams();
  const jobId = parseInt(id, 10);
  const navigate = useNavigate();
  const dialog = useDialog();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [job, setJob] = useState(null);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ intervalSeconds: '', maxEmailsPerLead: '', qualityScoreThreshold: '' });
  const [approveDialog, setApproveDialog] = useState({ open: false, item: null, subject: '', body: '', scheduledFor: '' });
  const [sendAllDialog, setSendAllDialog] = useState({ open: false, includeNeedsReview: false });
  const [previewDialog, setPreviewDialog] = useState({ open: false, item: null, detail: null, loading: false, error: '' });
  const pollRef = useRef(null);

  const loadJob = useCallback(async () => {
    try { const { data } = await getAutoOutreachRun(jobId); setJob(data); }
    catch (e) { setError(e?.response?.data?.message || 'Failed to load job'); }
  }, [jobId]);

  const loadItems = useCallback(async () => {
    try {
      const params = { skip: page * rowsPerPage, take: rowsPerPage };
      if (statusFilter && statusFilter !== 'All') params.status = statusFilter;
      const { data } = await listAutoOutreachItems(jobId, params);
      setItems(data || []);
    } catch (e) { setError(e?.response?.data?.message || 'Failed to load items'); }
  }, [jobId, page, rowsPerPage, statusFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadJob(), loadItems()]).finally(() => setLoading(false));
  }, [loadJob, loadItems]);

  useEffect(() => {
    if (!job || isTerminal(job.status)) return;
    pollRef.current = setInterval(async () => { await loadJob(); await loadItems(); }, 2000);
    return () => clearInterval(pollRef.current);
  }, [job, loadJob, loadItems]);

  const handleCancel = async (force = false) => {
    const ok = await dialog.confirm({
      title: force ? 'Force-cancel job' : 'Cancel job',
      message: force
        ? 'Force-cancel marks the job Cancelled immediately. Already-scheduled emails are NOT auto-unscheduled — open the lead\'s timeline to cancel each queued email manually.'
        : 'Stop after the current lead. Pending steps will be Skipped. Already-scheduled emails stay queued.',
      confirmText: force ? 'Force cancel' : 'Cancel job', danger: force,
    });
    if (!ok) return;
    try { await cancelAutoOutreachRun(jobId, force); await loadJob(); await loadItems(); }
    catch (e) { setError(e?.response?.data?.message || 'Cancel failed'); }
  };

  const handleResume = async () => {
    try { await resumeAutoOutreachRun(jobId); await loadJob(); await loadItems(); }
    catch (e) { setError(e?.response?.data?.message || 'Resume failed'); }
  };

  const handleDelete = async () => {
    const ok = await dialog.confirm({
      title: 'Delete outreach job',
      message: `Delete job #${jobId}? Job + per-step rows will be removed. Already-scheduled emails are NOT auto-cancelled.`,
      confirmText: 'Delete', danger: true,
    });
    if (!ok) return;
    try { await deleteAutoOutreachRun(jobId); navigate('/leads/auto-outreach'); }
    catch (e) { setError(e?.response?.data?.message || 'Delete failed'); }
  };

  const openEdit = () => {
    setEditForm({
      intervalSeconds: String(job?.intervalSeconds ?? ''),
      maxEmailsPerLead: String(job?.maxEmailsPerLead ?? ''),
      qualityScoreThreshold: String(job?.qualityScoreThreshold ?? ''),
    });
    setEditOpen(true);
  };
  const handleSaveEdit = async () => {
    try {
      const body = {};
      if (editForm.intervalSeconds !== '') body.intervalSeconds = parseInt(editForm.intervalSeconds, 10);
      if (editForm.maxEmailsPerLead !== '') body.maxEmailsPerLead = parseInt(editForm.maxEmailsPerLead, 10);
      if (editForm.qualityScoreThreshold !== '') body.qualityScoreThreshold = parseInt(editForm.qualityScoreThreshold, 10);
      await updateAutoOutreachRun(jobId, body);
      setEditOpen(false);
      await loadJob();
    } catch (e) { setError(e?.response?.data?.message || 'Update failed'); }
  };

  const openApprove = (it) => setApproveDialog({
    open: true, item: it,
    subject: it.subject || '',
    body: '',
    scheduledFor: it.scheduledFor ? new Date(it.scheduledFor).toISOString().slice(0, 16) : '',
  });
  const handleApprove = async () => {
    try {
      await approveAutoOutreachItem(jobId, approveDialog.item.id, {
        subject: approveDialog.subject || null,
        body: approveDialog.body || null,
        scheduledFor: approveDialog.scheduledFor ? new Date(approveDialog.scheduledFor).toISOString() : null,
      });
      setApproveDialog({ open: false, item: null, subject: '', body: '', scheduledFor: '' });
      await loadItems();
    } catch (e) { setError(e?.response?.data?.message || 'Approve failed'); }
  };

  const handleUnsubscribeFromRow = async (it) => {
    const hasContact = Boolean(it.leadContactId);
    const target = hasContact ? 'contact' : 'lead';
    const name = hasContact
      ? (it.contactName || it.contactEmail || `Contact #${it.leadContactId}`)
      : (`${it.companyName || `Lead #${it.leadId}`}`);
    const reason = await dialog.prompt({
      title: `Unsubscribe ${target}`,
      message: hasContact
        ? `Suppress outreach to ${name}. Pending emails to this contact (across all jobs) will be cancelled.`
        : `Suppress ALL outreach to ${name}. Pending emails for the whole company will be cancelled.`,
      label: 'Reason (optional)',
      confirmText: 'Unsubscribe', danger: true,
    });
    if (reason === null) return;
    try {
      const { data } = hasContact
        ? await unsubscribeLeadContact(it.leadId, it.leadContactId, reason || null)
        : await unsubscribeLead(it.leadId, reason || null);
      await dialog.alert({
        title: 'Unsubscribed',
        message: `Suppressed ${target}. Cancelled ${data?.cancelledInteractionCount ?? 0} pending email(s).`,
      });
      await loadItems();
    } catch (e) {
      setError(e?.response?.data?.message || 'Unsubscribe failed');
    }
  };

  const handleRegenerate = async (it) => {
    const ok = await dialog.confirm({
      title: 'Regenerate email',
      message: `Re-run AI personalize + improve + score for item #${it.id}? Subject and body will be replaced.`,
      confirmText: 'Regenerate',
    });
    if (!ok) return;
    try { await regenerateAutoOutreachItem(jobId, it.id); await loadItems(); }
    catch (e) { setError(e?.response?.data?.message || 'Regenerate failed'); }
  };

  const handleSendAllReady = async () => {
    try {
      const { data } = await sendAllReadyForJob(jobId, sendAllDialog.includeNeedsReview);
      setSendAllDialog({ open: false, includeNeedsReview: false });
      await loadJob(); await loadItems();
      await dialog.alert({
        title: 'Send all ready',
        message: `Promoted ${data.promoted} email(s) to ReadyToSend. Skipped (NeedsReview): ${data.skippedNeedsReview}. Already ready: ${data.alreadyReady}. Worker dispatches within ~30s.`,
      });
    } catch (e) { setError(e?.response?.data?.message || 'Send all failed'); }
  };

  const openPreview = async (it) => {
    setPreviewDialog({ open: true, item: it, detail: null, loading: true, error: '' });
    if (!it.interactionId) {
      setPreviewDialog((d) => ({ ...d, loading: false, error: 'Email not generated yet for this step.' }));
      return;
    }
    try {
      const { data } = await getInteractionDetail(it.interactionId);
      setPreviewDialog((d) => ({ ...d, detail: data, loading: false }));
    } catch (e) {
      setPreviewDialog((d) => ({ ...d, loading: false, error: e?.response?.data?.message || 'Failed to load email' }));
    }
  };
  const closePreview = () => setPreviewDialog({ open: false, item: null, detail: null, loading: false, error: '' });

  if (loading && !job) return <Box sx={{ p: 3 }}><Typography>Loading…</Typography></Box>;
  if (!job) return <Box sx={{ p: 3 }}><Alert severity="error">Job not found.</Alert></Box>;

  const progress = job.progressPercent ?? (job.totalLeads > 0 ? (100 * job.processedLeads / job.totalLeads) : 0);
  const canCancel = !isTerminal(job.status);
  const canResume = job.status === 'Cancelled' || job.status === 'Failed';
  const canEdit = !isTerminal(job.status) && job.status !== 'Cancelling';
  const canDelete = isTerminal(job.status);
  const canSendAll = !isTerminal(job.status) ? false : true; // only allowed when not running

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/leads/auto-outreach')}><BackIcon /></IconButton>
        <OutreachIcon color="primary" />
        <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.25rem' }}>Auto Outreach #{job.id}</Typography>
        <Chip size="small" label={job.status} color={STATUS_CHIP_COLOR[job.status] || 'default'} sx={{ fontWeight: 600 }} />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={loadJob} title="Refresh"><RefreshIcon fontSize="small" /></IconButton>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          {canCancel && (
            <Button size="small" variant="outlined" color="warning"
              startIcon={job.status === 'Cancelling' ? <CancellingIcon /> : <CancelIcon />}
              onClick={() => handleCancel(false)}
              disabled={job.status === 'Cancelling'}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}>
              {job.status === 'Cancelling' ? 'Cancelling…' : 'Cancel'}
            </Button>
          )}
          {canCancel && <Button size="small" variant="text" color="error" onClick={() => handleCancel(true)} sx={{ textTransform: 'none' }}>Force cancel</Button>}
          {canResume && <Button size="small" variant="contained" startIcon={<ResumeIcon />} onClick={handleResume} sx={{ textTransform: 'none', borderRadius: 1.5 }}>Resume</Button>}
          {canEdit && <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openEdit} sx={{ textTransform: 'none', borderRadius: 1.5 }}>Edit</Button>}
          {canSendAll && (
            <Button size="small" variant="contained" color="success" startIcon={<SendAllIcon />}
              onClick={() => setSendAllDialog({ open: true, includeNeedsReview: false })}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}>
              Send All Ready
            </Button>
          )}
          {canDelete && <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete} sx={{ textTransform: 'none', borderRadius: 1.5 }}>Delete</Button>}
        </Stack>

        <LinearProgress variant="determinate" value={Math.min(100, progress)} sx={{ height: 10, borderRadius: 1, mb: 1.5 }} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 3 }, mb: 1 }}>
          <Stat label="Template" value={job.templateName || `#${job.templateId}`} />
          <Stat label="Sequence steps" value={(job.sequence || []).length || 1} />
          <Stat label="Send mode" value={job.sendMode} />
          <Stat label="Max/Lead" value={job.maxEmailsPerLead} />
          <Stat label="Threshold" value={job.qualityScoreThreshold} />
          <Stat label="Interval" value={`${job.intervalSeconds}s`} />
          <Stat label="Total leads" value={job.totalLeads} />
          <Stat label="Processed" value={`${job.processedLeads} / ${job.totalLeads}`} />
          <Stat label="Succeeded" value={job.succeededLeads} color="#16a34a" />
          <Stat label="Failed" value={job.failedLeads} color={job.failedLeads > 0 ? '#dc2626' : '#94a3b8'} />
          <Stat label="Skipped" value={job.skippedLeads} color={job.skippedLeads > 0 ? '#f59e0b' : '#94a3b8'} />
          <Stat label="Emails scheduled" value={job.emailsScheduledTotal} color="#0284c7" bold />
          <Stat label="NeedsReview" value={job.emailsNeedReviewTotal} color={job.emailsNeedReviewTotal > 0 ? '#f59e0b' : '#94a3b8'} bold />
          <Stat label="Started" value={job.startedAt ? new Date(job.startedAt).toLocaleString() : '—'} />
          <Stat label="Duration" value={fmtDuration(job.startedAt, job.finishedAt, job.accumulatedSeconds)} />
        </Box>

        {job.errorMessage && <Alert severity="error" sx={{ mt: 1.5 }}>{job.errorMessage}</Alert>}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ color: '#475569', flex: 1 }}>Per-step results</Typography>
          <ToggleButtonGroup size="small" value={statusFilter} exclusive
            onChange={(_, v) => v && (setStatusFilter(v), setPage(0))}>
            {STATUS_FILTERS.map((s) => (
              <ToggleButton key={s} value={s} sx={{ textTransform: 'none', px: 1.25 }}>{s}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell align="center">Step</TableCell>
                <TableCell>Status</TableCell>
                {!isMobile && <TableCell>Subject</TableCell>}
                <TableCell align="right">Quality</TableCell>
                {!isMobile && <TableCell>Scheduled</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={isMobile ? 5 : 7} sx={{ color: '#94a3b8', fontStyle: 'italic' }}>No items match the current filter.</TableCell></TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      <RouterLink to={`/leads/${it.leadId}`} style={{ textDecoration: 'none', color: '#6366f1' }}>
                        #{it.leadId} {it.companyName}
                      </RouterLink>
                    </Typography>
                    {it.contactName && <Typography variant="caption" sx={{ color: '#64748b' }}>{it.contactName}{it.contactEmail ? ` · ${it.contactEmail}` : ''}</Typography>}
                  </TableCell>
                  <TableCell align="center"><Chip size="small" label={it.stepNumber} sx={{ height: 22 }} /></TableCell>
                  <TableCell><Chip size="small" label={it.status} color={ITEM_STATUS_COLOR[it.status] || 'default'} /></TableCell>
                  {!isMobile && <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.subject || <em style={{ color: '#94a3b8' }}>—</em>}</TableCell>}
                  <TableCell align="right">
                    {it.aiQualityScore != null
                      ? <Box component="span" sx={{ fontWeight: 700, color: it.aiQualityScore >= 80 ? '#16a34a' : it.aiQualityScore >= 60 ? '#0284c7' : '#dc2626' }}>{it.aiQualityScore}</Box>
                      : <em style={{ color: '#94a3b8' }}>—</em>}
                  </TableCell>
                  {!isMobile && <TableCell sx={{ fontSize: 12 }}>{it.scheduledFor ? new Date(it.scheduledFor).toLocaleString() : '—'}</TableCell>}
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={it.interactionId ? 'Preview email' : 'Email not generated yet'}>
                        <span>
                          <IconButton size="small" onClick={() => openPreview(it)} disabled={!it.interactionId}>
                            <PreviewIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {it.status === 'NeedsReview' && (
                        <Tooltip title="Approve & schedule"><IconButton size="small" color="success" onClick={() => openApprove(it)}><ApproveIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                      <Tooltip title="Regenerate (re-run AI)"><IconButton size="small" onClick={() => handleRegenerate(it)}><RegenerateIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={it.leadContactId ? 'Unsubscribe contact + cancel pending' : 'Unsubscribe lead + cancel pending'}>
                        <IconButton size="small" color="warning" onClick={() => handleUnsubscribeFromRow(it)}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {it.interactionId && (
                        <Tooltip title="Open lead"><IconButton size="small" component={RouterLink} to={`/leads/${it.leadId}`}><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <TablePagination component="div" count={-1} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelDisplayedRows={({ from, to }) => `${from}–${to}`} />
      </Paper>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit job</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" type="number" label="Interval (sec)" inputProps={{ min: 0, max: 3600 }}
              value={editForm.intervalSeconds} onChange={(e) => setEditForm((f) => ({ ...f, intervalSeconds: e.target.value }))} />
            <TextField size="small" type="number" label="Max emails/lead" inputProps={{ min: 1, max: 10 }}
              value={editForm.maxEmailsPerLead} onChange={(e) => setEditForm((f) => ({ ...f, maxEmailsPerLead: e.target.value }))} />
            <TextField size="small" type="number" label="Quality threshold" inputProps={{ min: 0, max: 100 }}
              value={editForm.qualityScoreThreshold} onChange={(e) => setEditForm((f) => ({ ...f, qualityScoreThreshold: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, item: null, subject: '', body: '', scheduledFor: '' })}
        maxWidth="md" fullWidth>
        <DialogTitle>Approve item #{approveDialog.item?.id}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" label="Subject (override)"
              value={approveDialog.subject} onChange={(e) => setApproveDialog((d) => ({ ...d, subject: e.target.value }))} />
            <TextField size="small" label="Body (override) — leave empty to keep current" multiline minRows={6}
              value={approveDialog.body} onChange={(e) => setApproveDialog((d) => ({ ...d, body: e.target.value }))} />
            <TextField size="small" type="datetime-local" label="Scheduled for (UTC)" InputLabelProps={{ shrink: true }}
              value={approveDialog.scheduledFor} onChange={(e) => setApproveDialog((d) => ({ ...d, scheduledFor: e.target.value }))} />
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Approving clears NeedsReview. The worker will pick the row up at ScheduledFor.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setApproveDialog({ open: false, item: null, subject: '', body: '', scheduledFor: '' })}>Cancel</Button>
          <Button size="small" variant="contained" color="success" onClick={handleApprove}>Approve</Button>
        </DialogActions>
      </Dialog>

      {/* Preview email dialog */}
      <Dialog open={previewDialog.open} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Email preview
          {previewDialog.item && (
            <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 400 }}>
              Item #{previewDialog.item.id} · Lead #{previewDialog.item.leadId} {previewDialog.item.companyName} · Step {previewDialog.item.stepNumber}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, md: 2 } }}>
          {previewDialog.loading && <Typography variant="body2" sx={{ color: '#64748b' }}>Loading…</Typography>}
          {previewDialog.error && <Alert severity="warning">{previewDialog.error}</Alert>}
          {previewDialog.detail && (
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Stat label="Status" value={previewDialog.detail.status} />
                <Stat label="Direction" value={previewDialog.detail.direction} />
                <Stat label="Channel" value={previewDialog.detail.channel} />
                {previewDialog.item?.aiQualityScore != null && (
                  <Stat label="Quality" value={previewDialog.item.aiQualityScore}
                    color={previewDialog.item.aiQualityScore >= 80 ? '#16a34a' : previewDialog.item.aiQualityScore >= 60 ? '#0284c7' : '#dc2626'} bold />
                )}
                <Stat label="Scheduled" value={previewDialog.detail.sentDate ? new Date(previewDialog.detail.sentDate).toLocaleString() : (previewDialog.item?.scheduledFor ? new Date(previewDialog.item.scheduledFor).toLocaleString() : '—')} />
              </Box>
              <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ px: 1.5, py: 1, bgcolor: '#f7fafc', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">Rendered email preview</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <Box component="span" sx={{ color: '#94a3b8', mr: 0.5 }}>To:</Box>
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {previewDialog.detail.contactName || previewDialog.item?.contactName || '—'}
                      {previewDialog.item?.contactEmail ? ` <${previewDialog.item.contactEmail}>` : ''}
                    </Box>
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
                    <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400, mr: 0.5 }}>Subject:</Box>
                    {previewDialog.detail.subject || '—'}
                  </Typography>
                </Box>
                <Box sx={{ height: 460, overflow: 'hidden' }}>
                  <iframe
                    title="Email preview"
                    srcDoc={buildPreviewDoc(previewDialog.detail.body || previewDialog.detail.bodyPreview || '')}
                    sandbox=""
                    style={{ width: '100%', height: '100%', border: 'none', display: 'block', backgroundColor: '#fff' }}
                  />
                </Box>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {previewDialog.item?.leadId && (
            <Button size="small" startIcon={<OpenInNewIcon fontSize="small" />}
              component={RouterLink} to={`/leads/${previewDialog.item.leadId}`}
              sx={{ textTransform: 'none' }}>Open lead</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Send all ready dialog */}
      <Dialog open={sendAllDialog.open} onClose={() => setSendAllDialog({ open: false, includeNeedsReview: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Send all ready</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Promotes every <strong>Draft</strong> in this job to <strong>ReadyToSend</strong>. Worker dispatches within ~30s. NeedsReview rows stay locked unless you opt in below.
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={sendAllDialog.includeNeedsReview} onChange={(e) => setSendAllDialog((d) => ({ ...d, includeNeedsReview: e.target.checked }))} />}
            label="Also include NeedsReview (low quality)" />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSendAllDialog({ open: false, includeNeedsReview: false })}>Cancel</Button>
          <Button size="small" variant="contained" color="success" onClick={handleSendAllReady}>Send all ready</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Stat({ label, value, color, bold }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 600, color: color || '#0f172a' }}>{value ?? '—'}</Typography>
    </Box>
  );
}
